import React, { useState, useEffect } from 'react';
import {
    Box,
    ColumnLayout,
    Container,
    ExpandableSection,
    Grid,
    Header,
    Icon,
    Pagination,
    Popover,
    Table,
} from '@amzn/awsui-components-react';
import TPPieChart from '../../shared/TPPieChart';
import { getReasonCodeDescription, getDisplayReasonCode, CREATE_TICKET_URL } from './util';

type ReportSummarySectionProps = {
    title: string;
    description: string;
    totalLabel: string;
    totalCount: number;
    successCount: number;
    failureCount: number;
    pendingCount: number;
    reasonCodeSummary?: Record<string, number>;
    failedItems: { id: string; reasonCode: string }[];
    failedItemIdHeader: string;
    failedItemsLabel: string;
    ariaDescription: string;
    ariaLabel: string;
    emptyDataDescription: string;
    noMatchDataDescription: string;
};

export default function ReportSummarySection(props: ReportSummarySectionProps) {
    const PAGE_SIZE = 20;
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => { setCurrentPage(1); }, [props.failedItems.length]);

    const calcPercentage = (count: number) =>
        props.totalCount ? (count / props.totalCount) * 100 : 0;


    const pieChartData = [
        { title: 'Success', percentage: calcPercentage(props.successCount), value: props.successCount },
        { title: 'Failed', percentage: calcPercentage(props.failureCount), value: props.failureCount },
        { title: 'In-Progress', percentage: calcPercentage(props.pendingCount), value: props.pendingCount },
    ].filter(slice => slice.value > 0);


    return (
        <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
            <Container header={
                <Header variant="h2" description={props.description}>
                    {props.title}
                </Header>}>
                <ColumnLayout columns={5} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">Success</Box>
                        <Box variant="h1">{props.successCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Failed</Box>
                        <Box variant="h1">{props.failureCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">In-Progress</Box>
                        <Box variant="h1">{props.pendingCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">{props.totalLabel}</Box>
                        <Box variant="h1">{props.totalCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Success Rate</Box>
                        <Box variant="h1">{calcPercentage(props.successCount).toFixed(1)}%</Box>
                    </div>
                </ColumnLayout>

                {props.failedItems.length > 0 && (
                    <ExpandableSection headerText={`${props.failedItemsLabel} (${props.failedItems.length})`} variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'id', header: props.failedItemIdHeader, cell: (item) => item.id },
                                { id: 'reasonCode', header: 'Reason Code', cell: (item) => (
                                    <Popover header={getDisplayReasonCode(item.reasonCode)} content={
                                        <span>
                                            {getReasonCodeDescription(item.reasonCode)} Please raise a{' '}
                                            <a href={CREATE_TICKET_URL} target="_blank" rel="noopener noreferrer">ticket</a> for investigation.
                                            <br/><b>CTI:</b> Finance Tech / FT Intercompany / IC Reporting
                                        </span>
                                    }>
                                        <Box color="text-status-info" fontSize="body-m">{getDisplayReasonCode(item.reasonCode)} <Icon name="status-info" size="small" /></Box>
                                    </Popover>
                                )},
                            ]}
                            items={props.failedItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
                            variant="embedded"
                            pagination={
                                props.failedItems.length > PAGE_SIZE ? (
                                    <Pagination
                                        currentPageIndex={currentPage}
                                        pagesCount={Math.ceil(props.failedItems.length / PAGE_SIZE)}
                                        onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                                    />
                                ) : undefined
                            }
                        />
                    </ExpandableSection>
                )}

                {props.reasonCodeSummary && Object.keys(props.reasonCodeSummary).length > 0 && (
                    <ExpandableSection headerText={`Failure Breakdown for ${props.title.replace(' Report Status', '')}`} variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'reason', header: 'Reason', cell: (item) => item.reason },
                                { id: 'count', header: 'Count', cell: (item) => item.count },
                            ]}
                            items={Object.entries(
                                Object.entries(props.reasonCodeSummary).reduce((acc, [code, count]) => {
                                    const display = getDisplayReasonCode(code);
                                    acc[display] = (acc[display] || 0) + (count as number);
                                    return acc;
                                }, {} as Record<string, number>)
                            ).map(([reason, count]) => ({ reason, count }))}
                            variant="embedded"
                        />
                    </ExpandableSection>
                )}

            </Container>
            <TPPieChart
                data={pieChartData}
                totalUnits={props.totalCount}
                ariaDescription={props.ariaDescription}
                ariaLabel={props.ariaLabel}
                emptyDataDescription={props.emptyDataDescription}
                noMatchDataDescription={props.noMatchDataDescription}
            />
        </Grid>
    );
}
